import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { ThemedText } from "@/components/themed-text";
import { Input } from "@/components/ui/input";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { Stack, useRouter } from "expo-router";
import React, { useState } from "react";
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View
} from "react-native";
import authService from "../../lib/auth";
import { showToast } from "../../lib/toast";

export default function ChangePasswordScreen() {
  const router = useRouter();
  const { theme } = useAuth();
  const desktop = useDesktopLayout();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showToast.error("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast.error("New passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      showToast.error("Password must be at least 8 characters");
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });
      showToast.success("Password changed successfully");
      // Navigate back after a short delay
      setTimeout(() => {
        router.back();
      }, 1500);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to change password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <DesktopAppFrame>
      <Stack.Screen options={{
        title: "Change Password",
        headerShown: !desktop,
        headerStyle: {
          backgroundColor: theme.background,
        },
        headerTintColor: theme.text,
        headerBackTitle: "Back",
      }} />
      {desktop && <ScreenHeader title="Change Password" showMenu={false} />}
      <ScrollView
        style={[styles.container, { backgroundColor: theme.background }]}
        contentContainerStyle={desktop ? styles.desktopContent : undefined}
      >
        <View style={styles.section}>
          <ThemedText style={styles.label} type="smallSemiBold">Current Password</ThemedText>
          <Input
            placeholder="Enter current password"
            value={currentPassword}
            onChangeText={setCurrentPassword}
            secureTextEntry
          />
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.label} type="smallSemiBold">New Password</ThemedText>
          <Input
            placeholder="Enter new password"
            value={newPassword}
            onChangeText={setNewPassword}
            secureTextEntry
          />
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.label} type="smallSemiBold">Confirm New Password</ThemedText>
          <Input
            placeholder="Confirm new password"
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry
          />
        </View>

        <TouchableOpacity
          style={[styles.saveButton, loading && styles.saveButtonDisabled]}
          onPress={handleChangePassword}
          disabled={loading}
        >
          <ThemedText style={styles.saveButtonText} type="defaultSemiBold">
            {loading ? "Changing..." : "Change Password"}
          </ThemedText>
        </TouchableOpacity>
      </ScrollView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  section: {
    padding: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    fontFamily: "PlusJakartaSans",
  },
  saveButton: {
    backgroundColor: BrandColors.primary,
    borderRadius: 12,
    padding: 16,
    margin: 20,
    alignItems: "center",
  },
  saveButtonDisabled: {
    backgroundColor: "#ccc",
  },
  saveButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
});
